import { useEffect, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Link } from 'react-router-dom';
import GameCard from '../components/game/GameCard';
import { useGamesStore } from '../store/useGamesStore';
import { GAME_CATEGORIES } from '../types/game';
import { FILL_GRADIENT } from '../constants/gradients';

export default function CategoriesPage() {
  const {
    games,
    isLoadingCatalog,
    catalogError,
    hasLoadedCatalog,
    fetchCatalog,
    getGamesByCategory,
  } = useGamesStore();

  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Fetch catalog on mount
  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Track scroll position to highlight active category
  useEffect(() => {
    const handleScroll = () => {
      const categories = GAME_CATEGORIES.filter(cat => cat.id !== 'All' && cat.id !== 'all');
      
      // Find which category section is currently at the top of the viewport
      let currentCategory = null;
      let minDistance = Infinity;
      
      for (const category of categories) {
        const element = document.getElementById(`category-${category.id}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.top - 150); // 150px offset for navbar + some buffer
          
          if (rect.top <= 200 && rect.bottom >= 150 && distance < minDistance) {
            minDistance = distance;
            currentCategory = category.id;
          }
        }
      }
      
      if (currentCategory) {
        setActiveCategory(currentCategory);
      }
    };

    // Initial check
    handleScroll();
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasLoadedCatalog]);

  // Scroll sidebar to keep active category in view with one-item buffer
  useEffect(() => {
    if (activeCategory) {
      const sidebarButton = document.getElementById(`sidebar-${activeCategory}`);
      const sidebar = document.getElementById('category-sidebar');
      
      if (sidebarButton && sidebar) {
        const buttonRect = sidebarButton.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();
        const itemHeight = sidebarButton.offsetHeight;
        const buffer = itemHeight; // One item height as buffer
        
        // Check if button is too close to top (within one item)
        if (buttonRect.top - sidebarRect.top < buffer) {
          sidebar.scrollBy({
            top: buttonRect.top - sidebarRect.top - buffer,
            behavior: 'auto'
          });
        }
        // Check if button is too close to bottom (within one item)
        else if (sidebarRect.bottom - buttonRect.bottom < buffer) {
          sidebar.scrollBy({
            top: buttonRect.bottom - sidebarRect.bottom + buffer,
            behavior: 'auto'
          });
        }
      }
    }
  }, [activeCategory]);

  // Scroll to category section
  const scrollToCategory = (categoryId: string) => {
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      const offset = 100; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'auto'
      });
      setActiveCategory(categoryId);
    }
  };

  // Error state
  if (catalogError && !hasLoadedCatalog) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-red-500 mb-4">Failed to load games</h2>
        <p className="text-muted mb-4">{catalogError}</p>
        <button
          onClick={() => fetchCatalog({}, true)}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Loading state
  if (isLoadingCatalog && !hasLoadedCatalog) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-12 h-12 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* SVG Gradient Definition */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#FCB902', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#FF00CF', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Browse by Category</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Discover games across all categories
        </p>
      </div>

      <div className="flex gap-8">
        {/* Left Sidebar - Category Navigation */}
        <aside id="category-sidebar" className="flex-shrink-0 w-24 sticky top-24 self-start z-50 max-h-[calc(100vh-7rem)] overflow-y-auto scrollbar-hide">
          <div className="space-y-3 pb-4">
            {GAME_CATEGORIES.filter(cat => cat.id !== 'All' && cat.id !== 'all').map((category) => {
              const categoryGames = getGamesByCategory(category.id as any);
              
              // Get icon component
              const IconComponent = (Icons as any)[category.icon] || Icons.Circle;
              const isActive = activeCategory === category.id;
              const hasGames = categoryGames.length > 0;
              
              return (
                <button
                  key={category.id}
                  id={`sidebar-${category.id}`}
                  onClick={() => scrollToCategory(category.id)}
                  className="w-full flex flex-col items-center gap-1.5 group"
                  title={category.displayName}
                >
                  <div className="relative w-16 h-16 rounded-lg flex items-center justify-center">
                    {/* Background layer - gray */}
                    <div className={`absolute inset-0 rounded-lg transition-opacity duration-300 ${
                      isActive 
                        ? 'opacity-0' 
                        : 'opacity-100 bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700'
                    }`} />
                    
                    {/* Gradient layer */}
                    <div 
                      className={`absolute inset-0 rounded-lg transition-opacity duration-300 ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{ background: FILL_GRADIENT }}
                    />
                    
                    {/* Icon */}
                    <IconComponent className={`w-9 h-9 relative z-10 transition-colors duration-300 ${
                      isActive 
                        ? 'text-white' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`} />
                  </div>
                  <span className={`text-xs font-medium text-center transition-colors duration-300 ${
                    isActive
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'
                  }`}>
                    {category.displayName}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main Content - Category Sections */}
        <div className="flex-1 space-y-12">

          {/* Category Sections */}
          {GAME_CATEGORIES.filter(cat => cat.id !== 'All' && cat.id !== 'all').map((category) => {
            const categoryGames = getGamesByCategory(category.id as any);
            const hasGames = categoryGames.length > 0;
            
            // Get icon component
            const IconComponent = (Icons as any)[category.icon] || Icons.Circle;
            
            return (
              <section key={category.id} id={`category-${category.id}`} className="space-y-4 scroll-mt-24">
                {/* Category Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconComponent 
                      className="w-8 h-8"
                      style={{ color: '#FF00CF' }}
                    />
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {category.displayName}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {categoryGames.length} {categoryGames.length === 1 ? 'game' : 'games'}
                      </p>
                    </div>
                  </div>
                  {hasGames && (
                    <Link
                      to={`/search?category=${category.id}`}
                      className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      View All
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>

                {/* Games or Empty State */}
                {hasGames ? (
                  <div className="relative">
                    <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
                      {categoryGames.slice(0, 8).map((game) => (
                        <div key={game.id} className="flex-none w-64">
                          <GameCard game={game} />
                        </div>
                      ))}
                      {categoryGames.length > 8 && (
                        <Link
                          to={`/search?category=${category.id}`}
                          className="flex-none w-64 h-full min-h-[400px] flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors group"
                        >
                          <ArrowRight className="w-12 h-12 text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors mb-3" />
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                            View All {categoryGames.length} Games
                          </p>
                        </Link>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <IconComponent className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400 font-medium">
                        No {category.displayName.toLowerCase()} games yet
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        Check back soon for new games!
                      </p>
                    </div>
                  </div>
                )}
              </section>
            );
          })}

          {/* Loading indicator when refreshing */}
          {isLoadingCatalog && hasLoadedCatalog && (
            <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
